<main>
  <loading-overlay [isLoading]="isLoading() > 0" [fullWidth]="true"></loading-overlay>
  <p-toast position="bottom-right" key="br"></p-toast>
  <p-confirmDialog />

  <header>
    <div class="headline row-start-center gap-[1rem]">
      <h1>{{ headerTitle() }}</h1>
      @if (!isNew()) {
        <p-tag [severity]="statusSeverity()" [value]="statusLabelText()" />
      }
    </div>
    <div class="buttons">
      <!-- #5223 — a draft has posted nothing, so it can be removed outright. Hidden until the record is
           saved (there is nothing to delete before that) and again once it leaves Draft. The API applies
           the same rule; this only keeps the button off screen when it would be refused. -->
      @if (isDraft() && currentId()) {
        <p-button [label]="'Delete' | translateWF: 'Delete'" icon="pi pi-trash" severity="danger" variant="outlined" (click)="confirmDelete()" />
      }

      <span class="separator"></span>
      @if (isDraft()) {
        <p-button [label]="'SaveDraft' | translateWF: 'Save Draft'" icon="pi pi-save" (click)="saveDraft()" />
      }
      @if (isDraft() && isPersisted()) {
        <p-button [label]="'Post' | translateWF: 'Post'" icon="pi pi-check" severity="success" (click)="post()" />
      }
      <!-- A posted document that should never have been posted. Reverses the entry and leaves the document
           terminally Cancelled — the correction is a NEW document, because posting may have consumed a
           self-issued CAI correlativo that is not handed back. Support role only; the API refuses while a
           payment, an advance, a Constancia, a note or an open payment request still stands against it. -->
      @if (canIssueNote()) {
        <p-button [label]="'ApDocument_NewCreditNote' | translateWF: 'New Credit Note'" icon="pi pi-minus-circle" variant="outlined" (click)="issueNote('CREDITNOTE')" />
        <p-button [label]="'ApDocument_NewDebitNote' | translateWF: 'New Debit Note'" icon="pi pi-plus-circle" variant="outlined" (click)="issueNote('DEBITNOTE')" />
      }
      @if (canOfferCancel()) {
        <p-button [label]="'ApDocument_Cancel' | translateWF: 'Cancel document'" icon="pi pi-ban" severity="danger" variant="outlined" (click)="openCancelDialog()" />
      }
      <p-button [label]="'Back' | translateWF: 'Back'" variant="outlined" icon="pi pi-arrow-left" (click)="back()" />
    </div>
  </header>

  <div class="content">
    <form [formGroup]="form" class="grid-12 gap-[1.5rem]">
      <!-- LEFT COLUMN -->
      <div class="span-12-12-9 col">
        <!-- Document -->
        <p-panel [header]="'ApDocument_Section_Document' | translateWF: 'Document'" [toggleable]="true">
          <p-card>
            <div class="grid-12">
              <!-- Pre-Invoice (#4306): the mode is chosen up-front via the "Pre Invoice" speed-dial action. -->
              @if (preInvoiceNumber(); as pin) {
                <div class="span-12">
                  <!-- #4767 — the badge names the source it was registered from (Order Invoice or Receipt). -->
                  <p-tag severity="info" [value]="preInvoiceBadgeLabel() + ': ' + pin" />
                </div>
              }

              <!-- Debit/Credit Note (#4473/#4477): pick the invoice this note adjusts — it seeds the provider,
                 currency and lines. On a loaded note the source shows as a badge. -->
              @if (isNote()) {
                <!-- #6324 — Motivo decides the line columns and how the API derives each line; Causa is for reports. -->
                <div class="span-12-6">
                  <div class="form-box">
                    <label for="noteReason">{{ 'ApDocument_Note_Reason' | translateWF: 'Reason' }} <span class="text-red-500">*</span></label>
                    <p-select
                      inputId="noteReason"
                      formControlName="noteReason"
                      [options]="noteReasonOptions()"
                      optionLabel="label"
                      optionValue="value"
                      [placeholder]="'ApDocument_Note_Reason_Placeholder' | translateWF: 'Choose the reason'"
                      [readonly]="noteReasonLocked()"
                      (onChange)="onNoteReasonChange($event.value)"
                      appendTo="body"
                      styleClass="w-full"
                    />
                    @if (noteReasonLocked() && isDraft()) {
                      <small class="text-surface-500">{{ 'ApDocument_Note_Reason_Locked' | translateWF: 'Locked once a line is checked — uncheck the lines to change it.' }}</small>
                    }
                  </div>
                </div>
                <div class="span-12-6">
                  <div class="form-box">
                    <label for="noteCause">
                      {{ isFinancialNote() ? ('ApDocument_Note_Concept' | translateWF: 'Concept') : ('ApDocument_Note_Cause' | translateWF: 'Cause') }}
                    </label>
                    <p-select
                      inputId="noteCause"
                      formControlName="noteCause"
                      [options]="noteCauseOptions()"
                      optionLabel="label"
                      optionValue="value"
                      [showClear]="true"
                      [placeholder]="'ApDocument_Note_Cause_Placeholder' | translateWF: 'Optional — for reports only'"
                      [readonly]="!isDraft()"
                      appendTo="body"
                      styleClass="w-full"
                    />
                  </div>
                </div>
              }
              @if (isNote() && !isFinancialNote()) {
                <div class="span-12-6">
                  <div class="form-box">
                    <label for="sourceInvoice">{{ 'ApDocument_Note_SourceDocument' | translateWF: 'Original invoice or receipt' }}</label>
                    @if (isNew() && isDraft()) {
                      <p-autoComplete
                        id="sourceInvoice"
                        [ngModel]="selectedSourceInvoice()"
                        [ngModelOptions]="{ standalone: true }"
                        #sourceInvoiceSearch="cslServerSearch"
                        [cslServerSearch]="fetchSourceInvoices"
                        [suggestions]="sourceInvoiceSearch.results()"
                        (onSelect)="onSourceInvoiceSelected($event.value)"
                        (onClear)="onSourceInvoiceCleared()"
                        optionLabel="number"
                        [forceSelection]="true"
                        [dropdown]="true"
                        dropdownIcon="pi pi-search"
                        [minLength]="1"
                        appendTo="body"
                        class="w-full"
                        styleClass="w-full"
                      >
                        <ng-template let-inv pTemplate="item">
                          <div class="col">
                            <span
                              ><strong>{{ inv.number }}</strong> — {{ inv.providerName }} · {{ inv.totalAmount | number: '1.2-2' }}</span
                            >
                          </div>
                        </ng-template>
                      </p-autoComplete>
                    } @else {
                      <span class="rate-readout">{{ noteSourceNumber() || documentNumber() }}</span>
                    }
                  </div>
                </div>
              }
              <!-- #6324 — warnings only, never a block (P13/P14). -->
              @if (isNote() && noteOriginalHasPostedCertificate()) {
                <div class="span-12">
                  <p-message
                    severity="warn"
                    styleClass="w-full"
                    [text]="'ApDocument_Note_PostedCertificateWarning' | translateWF: 'The original already has a posted withholding certificate. This note does not adjust it.'"
                  />
                </div>
              }
              @if (noteServiceMonthMismatch()) {
                <div class="span-12">
                  <p-message
                    severity="warn"
                    styleClass="w-full"
                    [text]="
                      'ApDocument_Note_ServiceMonthWarning'
                        | translateWF
                          : 'This credit note covers service lines and is dated in a different month from its invoice. The provider should issue it in the month of the service, or re-invoice.'
                    "
                  />
                </div>
              }

              <!-- Purchase Receipt (#4465): expected-next-number banner — informational; the draw happens on Post. -->
              @if (isPurchaseReceipt() && isDraft()) {
                <div class="span-12 row-sm-start-center flex-wrap">
                  @if (purchaseReceiptPreview(); as bp) {
                    <p-tag
                      [severity]="bp.remainingInRange < 20 ? 'warn' : 'info'"
                      [value]="('ApDocument_PurchaseReceipt_ExpectedNumber' | translateWF: 'Expected next number') + ': ' + bp.nextNumber"
                    />
                    <small class="text-surface-500">
                      CAI {{ bp.cai }} · {{ bp.remainingInRange }} {{ 'ApDocument_PurchaseReceipt_Remaining' | translateWF: 'remaining' }} ·
                      {{ 'ApDocument_PurchaseReceipt_Until' | translateWF: 'until' }} {{ bp.endDate | date: 'dd/MM/yyyy' }}
                    </small>
                  } @else if (purchaseReceiptPreviewError()) {
                    <p-tag severity="warn" [value]="purchaseReceiptPreviewError()!" />
                  }
                </div>
              }

              @if (!usePreInvoice() && (!isNote() || isFinancialNote())) {
                <!-- Full width so long provider names fit the typeahead (and its dropdown). -->
                <div class="span-12">
                  <div class="form-box">
                    <!-- Every AP document has a Business master record — one-time (name-only) vendors were
                       removed in #4785; the API rejects a document without a ProviderReference. -->
                    <label for="provider">{{ 'ApDocument_Provider' | translateWF: 'Provider' }}</label>
                    <p-autoComplete
                      id="provider"
                      formControlName="provider"
                      #providerSearch="cslServerSearch"
                      [cslServerSearch]="fetchProviders"
                      [suggestions]="providerSearch.results()"
                      (onSelect)="onProviderSelected($event.value)"
                      (onClear)="onProviderCleared()"
                      optionLabel="name"
                      [forceSelection]="true"
                      [dropdown]="true"
                      dropdownIcon="pi pi-search"
                      [minLength]="1"
                      appendTo="body"
                      class="w-full"
                      styleClass="w-full"
                      [ngClass]="{ 'ng-invalid ng-dirty': form.controls.provider.invalid && form.controls.provider.touched }"
                    >
                      <ng-template let-business pTemplate="item">
                        <div class="col">
                          <span
                            ><strong>{{ business.code }}</strong> — {{ business.name }}</span
                          >
                        </div>
                      </ng-template>
                    </p-autoComplete>
                    @if (selectedProvider(); as prov) {
                      <div class="row-sm-between-center mt-1">
                        <small class="text-surface-500 block"
                          ><strong>{{ prov.code }}</strong> — {{ prov.name }}</small
                        >
                        <!-- #4727 — re-fetch the provider (e.g. after its AP account was filled in) without clearing lines. -->
                        @if (isDraft()) {
                          <p-button
                            [label]="'ApDocument_ReloadProvider' | translateWF: 'Reload'"
                            icon="pi pi-refresh"
                            size="small"
                            variant="text"
                            (click)="reloadProvider()"
                            [pTooltip]="'ApDocument_ReloadProvider_Tip' | translateWF: 'Re-fetch this provider (e.g. after setting its AP account) without clearing the lines.'"
                          />
                        }
                      </div>
                    }
                  </div>
                </div>
              } @else if (usePreInvoice()) {
                <div class="span-12-6">
                  <div class="form-box">
                    <label for="preInvoice">{{ 'ApDocument_PreInvoice_Select' | translateWF: 'Register from' }}</label>
                    <!-- The Invoice / Receipt source toggle (#4767) is no longer offered: a Pre-Invoice is
                       registered from an Order Invoice, full stop. Everything behind the receipt source is
                       intentionally left in place — `searchPreInvoice` still branches on it, and a document
                       already registered from a receipt still reopens with its badge and its locked lines.
                       Restoring the choice is putting the p-selectButton back, not rebuilding a flow. -->
                    <p-autoComplete
                      id="preInvoice"
                      [ngModel]="selectedPreInvoice()"
                      [ngModelOptions]="{ standalone: true }"
                      #preInvoiceSearch="cslServerSearch"
                      [cslServerSearch]="fetchPreInvoices"
                      [suggestions]="preInvoiceSearch.results()"
                      (onSelect)="onPreInvoiceSelected($event.value)"
                      (onClear)="onPreInvoiceCleared()"
                      optionLabel="number"
                      [forceSelection]="true"
                      [dropdown]="true"
                      dropdownIcon="pi pi-search"
                      [minLength]="1"
                      appendTo="body"
                      styleClass="w-full"
                      [placeholder]="'ApDocument_PreInvoice_SearchHint' | translateWF: 'Search by number, provider invoice # or fiscal #…'"
                    >
                      <ng-template let-source pTemplate="item">
                        <div class="col">
                          <span
                            ><strong>{{ source.number }}</strong> — {{ source.totalAmount | number: '1.2-2' }}</span
                          >
                          @if (source.providerInvoiceNumber || source.fiscalInvoiceNumber) {
                            <small class="text-surface-500">
                              @if (source.providerInvoiceNumber) {
                                {{ 'ApDocument_PreInvoice_ProviderNumber' | translateWF: 'Provider #' }}: {{ source.providerInvoiceNumber }}
                              }
                              @if (source.fiscalInvoiceNumber) {
                                · {{ 'ApDocument_PreInvoice_FiscalNumber' | translateWF: 'Fiscal #' }}: {{ source.fiscalInvoiceNumber }}
                              }
                            </small>
                          }
                        </div>
                      </ng-template>
                    </p-autoComplete>
                    @if (selectedPreInvoice()?.purchaseOrderNumbers?.length) {
                      <small class="text-surface-500"> {{ 'ApDocument_PreInvoice_Po' | translateWF: 'PO' }}: {{ selectedPreInvoice()?.purchaseOrderNumbers?.join(', ') }} </small>
                    } @else if (selectedPreInvoice()?.fiscalInvoiceNumber) {
                      <small class="text-surface-500">
                        {{ 'ApDocument_PreInvoice_FiscalNumber' | translateWF: 'Fiscal #' }}: {{ selectedPreInvoice()?.fiscalInvoiceNumber }}
                      </small>
                    } @else {
                      <small class="text-surface-500">{{
                        'ApDocument_PreInvoice_Hint'
                          | translateWF
                            : 'Provider and lines are
                    filled from the source on save.'
                      }}</small>
                    }
                    <!-- Provider is resolved from the pre-invoice's POs — show who it landed on (code — name). -->
                    @if (selectedProvider(); as prov) {
                      <div class="row-sm-between-center mt-1">
                        <small class="text-surface-600 block">
                          {{ 'ApDocument_Provider' | translateWF: 'Provider' }}: <strong>{{ prov.code }}</strong> — {{ prov.name }}
                        </small>
                        <!-- #5231 — the same reload the ordinary path has had since #4727. The provider itself
                           stays underived-from-here (it belongs to the pre-invoice's POs), so this refreshes
                           the master's details in place rather than offering a different provider. -->
                        @if (isDraft()) {
                          <p-button
                            [label]="'ApDocument_ReloadProvider' | translateWF: 'Reload'"
                            icon="pi pi-refresh"
                            size="small"
                            variant="text"
                            (click)="reloadProvider()"
                            [pTooltip]="'ApDocument_ReloadProvider_Tip' | translateWF: 'Re-fetch this provider (e.g. after setting its AP account) without clearing the lines.'"
                          />
                        }
                      </div>
                    }
                  </div>
                </div>
              }
              <!-- #6324 — ORIGINAL DOCUMENT: what the note adjusts. The note takes its provider and AP account from
                 it, so they are shown here rather than under Provider & terms. -->
              @if (isNote() && !isFinancialNote() && noteOriginal(); as original) {
                <div class="span-12">
                  <p-panel [header]="('ApDocument_Section_OriginalDocument' | translateWF: 'Original document') + ': ' + original.number">
                    <ng-template pTemplate="icons">
                      <div class="row-sm-start-center">
                        <p-tag [severity]="statusSeverity(original.status)" [value]="statusLabelText(original.status)" />
                        <!-- #7050 — the original's full detail in the side panel, read-only, without leaving the note. -->
                        <p-button
                          [label]="'ApDocument_Note_ViewOriginal' | translateWF: 'View original'"
                          icon="pi pi-eye"
                          size="small"
                          variant="outlined"
                          severity="secondary"
                          [pTooltip]="'ApDocument_QuickView_Open' | translateWF: 'View document detail'"
                          (click)="openNoteOriginal()"
                        />
                      </div>
                    </ng-template>
                    <p-card>
                      <div class="provider-info mb-2">
                        <span class="provider-info__label">
                          @if (original.documentType === ApDocumentType.Receipt) {
                            {{ 'ApDocument_ReceiptNumber' | translateWF: 'Receipt #' }}
                          } @else {
                            {{ 'ApDocument_FiscalInvoiceNumber' | translateWF: 'Fiscal Invoice Number' }}
                          }
                        </span>
                        <span class="provider-info__value">{{ original.fiscalNumber || '—' }}</span>
                      </div>
                      <p class="ap-provider-line">
                        <span class="ap-provider-line__code">{{ original.providerCode }}</span>
                        <span class="ap-provider-line__name">{{ original.providerName }}</span>
                      </p>
                      <div class="grid-12-sm provider-info">
                        <div class="span-6-3">
                          <span class="provider-info__label">{{ 'ApDocument_TaxId' | translateWF: 'Tax ID' }}</span>
                          <span class="provider-info__value">{{ original.providerTaxId || providerTaxId || '—' }}</span>
                        </div>
                        <div class="span-6-3">
                          <span class="provider-info__label">{{ 'ApDocument_CreditDays' | translateWF: 'Credit Days' }}</span>
                          <span class="provider-info__value">{{ original.creditDays }} {{ 'ApDocument_Days' | translateWF: 'days' }}</span>
                        </div>
                        <div class="span-12-6">
                          <span class="provider-info__label">{{ 'ApDocument_ApAccount' | translateWF: 'AP Account' }}</span>
                          <span class="provider-info__value">{{ (original.apAccountName | localized) || original.apAccountNumber || '—' }}</span>
                          @if ((original.apAccountName | localized) && original.apAccountNumber) {
                            <small class="provider-info__sub">{{ original.apAccountNumber }}</small>
                          }
                        </div>
                        <div class="span-6-3">
                          <span class="provider-info__label">{{ 'ApDocument_DocumentDate' | translateWF: 'Document date' }}</span>
                          <span class="provider-info__value">{{ original.documentDate | date: 'dd/MM/yyyy' : 'UTC' }}</span>
                        </div>
                        <div class="span-6-3">
                          <span class="provider-info__label">{{ 'ApDocument_DueDate' | translateWF: 'Due date' }}</span>
                          <span class="provider-info__value">{{ original.dueDate | date: 'dd/MM/yyyy' : 'UTC' }}</span>
                        </div>
                      </div>
                    </p-card>
                  </p-panel>
                </div>
              }
              <!-- #4785 — PROVIDER & TERMS: who we owe and on what terms. Everything here is derived from the
                 provider master or the source document, so it is read-only apart from the term override. -->
              <div class="span-12">
                <p-panel [header]="'ApDocument_Section_ProviderTerms' | translateWF: 'Provider & terms'">
                  <ng-template pTemplate="icons">
                    <span class="text-sm text-[var(--p-text-muted-color)]">{{
                      'ApDocument_ProviderTerms_Fixed'
                        | translateWF
                          : 'Fixed once the
                      document is created'
                    }}</span>
                  </ng-template>
                  <p-card>
                    <!-- #6324 — a note against an original shows its provider in the Original document panel. -->
                    @if (!isNote() || isFinancialNote()) {
                      @if (selectedProvider(); as prov) {
                        <p class="ap-provider-line">
                          <span class="ap-provider-line__code">{{ prov.code }}</span>
                          <span class="ap-provider-line__name">{{ prov.name }}</span>
                        </p>
                      }

                      <!-- Tax ID and Credit Days split the left half (3 + 3) so AP Account can take the right half
                     and line up with Due date in the row beneath it. -->
                      <div class="grid-12-sm provider-info">
                        <div class="span-6-3">
                          <span class="provider-info__label">{{ 'ApDocument_TaxId' | translateWF: 'Tax ID' }}</span>
                          <span class="provider-info__value">{{ providerTaxId || '—' }}</span>
                        </div>
                        @if (!isCreditNote()) {
                          <div class="span-6-3">
                            <span class="provider-info__label">{{ 'ApDocument_CreditDays' | translateWF: 'Credit Days' }}</span>
                            <!-- #4784 — the days actually driving the due date: resolved from the document's payment
                         term (source PO / Receipt may beat the provider default), not the provider's alone. -->
                            <span class="provider-info__value">{{ effectiveCreditDays }} {{ 'ApDocument_Days' | translateWF: 'days' }}</span>
                          </div>
                        }
                        <div class="span-12-6">
                          <span class="provider-info__label">{{ 'ApDocument_ApAccount' | translateWF: 'AP Account' }}</span>
                          <!-- #4785 — lead with the account name; the number is the subtitle. Falls back to the
                         number alone for providers saved before the name was derived API-side.
                         The account shown is the one the DOCUMENT is pinned to, not the provider's current
                         one — the icon appears when those two have drifted apart. -->
                          <span class="provider-info__value">
                            {{ (effectiveApAccountName | localized) || effectiveApAccountNumber || '—' }}
                            @if (apAccountDiffersFromProvider()) {
                              <i class="pi pi-info-circle text-xs text-orange-500 ml-1" [pTooltip]="apAccountDivergenceTooltip" tooltipPosition="top"></i>
                            }
                          </span>
                          @if ((effectiveApAccountName | localized) && effectiveApAccountNumber) {
                            <small class="provider-info__sub">{{ effectiveApAccountNumber }}</small>
                          }
                          @if (apAccountDiffersFromProvider()) {
                            <small class="provider-info__sub text-orange-500">
                              {{
                                'ApDocument_ApAccount_DiffersShort'
                                  | translateWF
                                    : 'Differs from the provider’s current
                        account'
                              }}
                            </small>
                          }
                        </div>
                      </div>
                    }

                    @if (isCreditNote()) {
                      <!-- #6324 — a credit note is applied to its original (or kept as provider credit), never paid. -->
                      <p-message
                        severity="info"
                        styleClass="w-full"
                        [ngClass]="{ 'mt-3': isFinancialNote() }"
                        [text]="'ApDocument_CreditNote_NoTerm' | translateWF: 'A credit note is applied, not paid: it has no payment term or credit days.'"
                      />
                    } @else {
                      <div class="grid-12" [class.mt-3]="!isNote() || isFinancialNote()">
                        <div class="span-12-6">
                          <div class="form-box">
                            <label for="paymentTermCode" class="row-sm-between-center">
                              <span>
                                {{ 'ApDocument_PaymentTerm' | translateWF: 'Payment term' }}
                                <span class="text-xs text-surface-400">({{ paymentTermSourceLabel }})</span>
                              </span>
                              <!-- Override is offered ONLY when no source document dictates the term. When one does,
                             the field stays locked and the tooltip says where to correct it instead. -->
                              @if (canOverridePaymentTerm() && !paymentTermOverrideOn()) {
                                <a
                                  role="button"
                                  tabindex="0"
                                  class="ap-link"
                                  (click)="onOverridePaymentTerm()"
                                  (keydown.enter)="onOverridePaymentTerm()"
                                  (keydown.space)="onOverridePaymentTerm()"
                                  >{{ 'ApDocument_PaymentTerm_Override' | translateWF: 'Override' }}</a
                                >
                              } @else if (!canOverridePaymentTerm() && !paymentTermOverrideOn()) {
                                <i class="pi pi-info-circle text-xs" [pTooltip]="paymentTermTooltip" tooltipPosition="top"></i>
                              }
                            </label>
                            <csl-dropdown-master-data id="paymentTermCode" class="w-full" categoryCode="PaymentTermsProvider" formControlName="paymentTermCode" />
                            @if (paymentTermDiffersFromProvider()) {
                              <small class="text-surface-500 block mt-1">
                                {{
                                  'ApDocument_PaymentTerm_DiffersFromProvider'
                                    | translateWF
                                      : 'This term comes from the
                          source document and differs from the provider default.'
                                }}
                              </small>
                            }
                          </div>
                        </div>
                        <div class="span-12-6">
                          <div class="form-box">
                            <label for="dueDate">
                              {{ 'ApDocument_DueDate' | translateWF: 'Due date' }}
                              <span class="text-xs text-surface-400">{{ 'ApDocument_DueDate_Auto' | translateWF: 'auto' }}</span>
                            </label>
                            <p-datepicker id="dueDate" formControlName="dueDate" [showIcon]="true" appendTo="body" styleClass="w-full" />
                            @if (dueDateDerivation) {
                              <small class="text-surface-500 block mt-1">{{ dueDateDerivation }}</small>
                            }
                          </div>
                        </div>
                      </div>
                    }
                  </p-card>
                </p-panel>
              </div>

              <!-- #4785 — DATES & FISCAL IDENTITY: when it hits the GL, and which fiscal document it is. -->
              <div class="span-12">
                <p-panel [header]="'ApDocument_Section_DatesFiscal' | translateWF: 'Dates & fiscal identity'">
                  <ng-template pTemplate="footer">
                    @if (receivedDateMode() === 'grn') {
                      <small class="text-surface-500">
                        {{
                          'ApDocument_ReceivedDate_FromGrn'
                            | translateWF
                              : 'Days goods were received against this
                        document’s purchase orders.'
                        }}
                      </small>
                    }
                  </ng-template>
                  <p-card>
                    <div class="grid-12">
                      <!-- Self-issued fiscal documents (#4465/#4526): Purchase Receipt + Debit/Credit Notes draw their
                 fiscal number + CAI from the company's own range on Post — no inputs while drafting; shown
                 read-only once posted. Provider (incoming) types still supply their own #/CAI.
                 CAI sits directly under Payment term and matches its width; the provider number is beside it. -->
                      @if (!isSelfIssued() || !isDraft()) {
                        <!-- A Policy (#6016) never carries a CAI, so only its policy number shows. -->
                        @if (!isPolicy()) {
                          <div class="span-12-6">
                            <div class="form-box">
                              <label for="cai">
                                {{ 'ApDocument_Cai' | translateWF: 'CAI' }}
                                <!-- #5029 — required for every provider-issued kind, notes included, when the provider's
                         domicile issues authorizations. The options are already narrowed to CAIs covering
                         this document type, so the only selectable value is a valid one. -->
                                @if (requiresCai()) {
                                  <span class="text-red-500">*</span>
                                }
                              </label>
                              @if (isSelfIssued()) {
                                <!-- The company's own issuing CAI (stamped on post) — not a provider CAI option. -->
                                <input id="cai" type="text" formControlName="cai" pInputText class="w-full" readonly />
                              } @else if (providerCountryHasNoAuthorization()) {
                                <!-- CAI is a Honduras fiscal requirement (#4726). A provider domiciled elsewhere has no
                         CAI, so the provider invoice number beside it is accepted as a free number. -->
                                <div class="text-sm text-surface-500 flex items-start gap-1 mt-1">
                                  <i class="pi pi-info-circle text-surface-400 mt-[0.15rem]"></i>
                                  <span>{{
                                    'ApDocument_Cai_NoAuthorizationRegime'
                                      | translateWF
                                        : 'A fiscal authorization applies only when
                      the provider is domiciled in a country that issues one.'
                                  }}</span>
                                </div>
                              } @else {
                                <p-select
                                  id="cai"
                                  formControlName="cai"
                                  [options]="caiOptions()"
                                  optionLabel="label"
                                  optionValue="value"
                                  [showClear]="true"
                                  [filter]="true"
                                  appendTo="body"
                                  styleClass="w-full"
                                  [placeholder]="'ApDocument_Cai_Select' | translateWF: 'Select a CAI'"
                                />
                              }
                            </div>
                          </div>
                        }
                        <div class="span-12-6">
                          <div class="form-box">
                            <label for="fiscalNumber">
                              @if (isPurchaseReceipt()) {
                                {{ 'ApDocument_PurchaseReceiptNumber' | translateWF: 'Purchase Receipt #' }}
                              } @else if (isDebitNote()) {
                                {{ 'ApDocument_DebitNoteNumber' | translateWF: 'Debit Note #' }}
                              } @else if (isCreditNote()) {
                                {{ 'ApDocument_CreditNoteNumber' | translateWF: 'Credit Note #' }}
                              } @else if (isReceipt()) {
                                {{ 'ApDocument_ReceiptNumber' | translateWF: 'Receipt #' }}
                              } @else if (isPolicy()) {
                                {{ 'ApDocument_PolicyNumber' | translateWF: 'Policy #' }}
                              } @else {
                                {{ 'ApDocument_FiscalInvoiceNumber' | translateWF: 'Fiscal Invoice Number' }}
                              }
                              <!-- #4890 — the number is range-checked against the CAI on Post, so it has to be here
                         before the document is saved, not discovered missing at posting time. -->
                              @if (requiresFiscalNumber()) {
                                <span class="text-red-500">*</span>
                              }
                            </label>
                            <input
                              id="fiscalNumber"
                              type="text"
                              formControlName="fiscalNumber"
                              pInputText
                              class="w-full"
                              [readonly]="isSelfIssued()"
                              [ngClass]="{ 'ng-invalid ng-dirty': form.controls.fiscalNumber.invalid && form.controls.fiscalNumber.touched }"
                            />
                          </div>
                        </div>

                        <!-- The provider's OWN reference (#5029) — their equivalent of our AP-YYYY-NNNNNN. Sits beside
                   the fiscal number rather than replacing it: only the fiscal number is range-checked
                   against the CAI, and this one is never validated. Always editable, including on a
                   self-issued Purchase Receipt, where the provider may still have given us a reference. -->
                        <div class="span-12-6">
                          <div class="form-box">
                            <label for="providerReferenceNumber">
                              {{ 'ApDocument_ProviderReferenceNumber' | translateWF: 'Provider Reference #' }}
                            </label>
                            <input id="providerReferenceNumber" type="text" formControlName="providerReferenceNumber" pInputText class="w-full" />
                          </div>
                        </div>
                      }

                      <!-- Dates on their own row, so the two warnings below them never push the fiscal fields around. -->
                      <div class="span-12-6">
                        <div class="form-box">
                          <label for="documentDate">{{ 'ApDocument_DocumentDate' | translateWF: 'Document Date' }}</label>
                          <p-datepicker
                            id="documentDate"
                            formControlName="documentDate"
                            [showIcon]="true"
                            appendTo="body"
                            styleClass="w-full"
                            [ngClass]="{ 'ng-invalid ng-dirty': form.controls.documentDate.invalid && form.controls.documentDate.touched }"
                          />
                          <!-- #4783 — advisory only: a stale vendor invoice is flagged in red but never blocked. -->
                          @if (invoiceAgeWarning()) {
                            <p-message severity="error" styleClass="w-full mt-2" [text]="invoiceAgeWarning()!" />
                          }
                        </div>
                      </div>

                      <!-- #5165 — when the goods arrived, which is what the due date is measured from. Captured three
                 different ways depending on where the date can actually be known from. -->
                      <div class="span-12-6">
                        <div class="form-box">
                          <label for="receivedDate">
                            {{ 'ApDocument_ReceivedDate' | translateWF: 'Received Date' }}
                            @if (receivedDateRequired()) {
                              <span class="text-red-500">*</span>
                            }
                          </label>

                          @switch (receivedDateMode()) {
                            @case ('shipment') {
                              <!-- The invoice belongs to one shipment and that shipment records when it was completed,
                         so the API owns this date. Read-only rather than absent: the clerk still needs to see
                         what the due date is being measured from. -->
                              <p-datepicker id="receivedDate" formControlName="receivedDate" [showIcon]="true" appendTo="body" styleClass="w-full" />
                              @if (receivedDatePendingShipment()) {
                                <!-- Legitimately empty: the goods are still in transit. Saying so beats a blank
                           read-only box the clerk cannot fill and has no explanation for. -->
                                <small class="text-surface-500 block mt-1">
                                  {{
                                    'ApDocument_ReceivedDate_PendingShipment'
                                      | translateWF
                                        : 'The shipment has not been received yet
                    — this date fills in on its own once it is.'
                                  }}
                                </small>
                              } @else {
                                <small class="text-surface-500 block mt-1">
                                  {{
                                    'ApDocument_ReceivedDate_FromShipment'
                                      | translateWF
                                        : 'Taken from the shipment this invoice was
                    received in.'
                                  }}
                                </small>
                              }
                            }
                            @case ('grn') {
                              <p-select
                                id="receivedDate"
                                formControlName="receivedDate"
                                [options]="receivedDateOptionItems()"
                                optionLabel="label"
                                optionValue="value"
                                appendTo="body"
                                styleClass="w-full"
                                [placeholder]="'ApDocument_ReceivedDate_Select' | translateWF: 'Select a receiving date'"
                                [ngClass]="{ 'ng-invalid ng-dirty': form.controls.receivedDate.invalid && form.controls.receivedDate.touched }"
                              />
                            }
                            @default {
                              <p-datepicker
                                id="receivedDate"
                                formControlName="receivedDate"
                                [showIcon]="true"
                                appendTo="body"
                                styleClass="w-full"
                                [ngClass]="{ 'ng-invalid ng-dirty': form.controls.receivedDate.invalid && form.controls.receivedDate.touched }"
                              />
                              @if (receivedDateNoOptionsHint()) {
                                <small class="text-surface-500 block mt-1">
                                  {{
                                    'ApDocument_ReceivedDate_NoGrn'
                                      | translateWF
                                        : 'Nothing has been receipted against these purchase
                    orders yet — enter the date the goods arrived.'
                                  }}
                                </small>
                              }
                            }
                          }

                          @if (form.controls.receivedDate.hasError('receivedBeforeDocument')) {
                            <p-message
                              severity="error"
                              styleClass="w-full mt-2"
                              [text]="'ApDocument_ReceivedDate_BeforeDocument' | translateWF: 'The received date cannot be earlier than the document date.'"
                            />
                          }
                        </div>
                      </div>

                      <div class="span-12-6">
                        <div class="form-box">
                          <label for="postingDate">{{ 'ApDocument_PostingDate' | translateWF: 'Posting Date' }}</label>
                          <p-datepicker
                            id="postingDate"
                            formControlName="postingDate"
                            [showIcon]="true"
                            appendTo="body"
                            styleClass="w-full"
                            [ngClass]="{ 'ng-invalid ng-dirty': form.controls.postingDate.invalid && form.controls.postingDate.touched }"
                          />
                          @if (postingDateWarning()) {
                            <p-message severity="warn" styleClass="w-full mt-2" [text]="postingDateWarning()!" />
                          }
                        </div>
                      </div>
                    </div>
                  </p-card>
                </p-panel>
              </div>
            </div>
          </p-card>
        </p-panel>

        <!-- #4785 — Purchase orders get their own collapsible section above the lines, so it can be folded
             away once the POs are picked and the line grid gets the full panel. Invoice only: it is the only
             kind whose lines come from POs. Pre-invoice documents have their lines from the source. -->
        @if (useItemTypeAhead() && !isPreInvoice()) {
          <p-panel [toggleable]="true">
            <ng-template pTemplate="header">
              <div class="row-between-center w-full">
                <span class="font-semibold">{{ 'ApDocument_Section_PurchaseOrders' | translateWF: 'Purchase orders' }}</span>
                @if (addedPoIds().size) {
                  <span class="text-xs text-surface-500"> {{ addedPoIds().size }} {{ 'ApDocument_PoAddedCount' | translateWF: 'added' }} </span>
                }
              </div>
            </ng-template>

            <p-card>
              @if (selectedProvider()) {
                <div class="po-picker">
                  <div class="po-picker__bar">
                    <span class="p-input-icon-left po-picker__search">
                      <i class="pi pi-search"></i>
                      <input
                        type="text"
                        pInputText
                        [formControl]="poSearchControl"
                        [placeholder]="'ApDocument_SearchPo' | translateWF: 'Search a purchase order to add its items…'"
                        class="w-full"
                      />
                    </span>
                    <span class="po-picker__scope">{{ 'ApDocument_OpenPos' | translateWF: 'Open POs' }} · {{ selectedProvider()?.name }}</span>
                  </div>

                  @if (isDraft()) {
                    <div class="po-list">
                      <!-- visiblePos pins the POs already on the document ahead of this page's results, so
                         searching or paging can never hide something already added. -->
                      @for (po of visiblePos(); track po.id) {
                        <div class="po-list__row" [class.po-list__row--added]="isPoAdded(po.id)">
                          <div class="po-list__main">
                            <a class="po-list__number">{{ po.purchaseOrderNumber }}</a>
                            <span class="po-list__meta">
                              {{ po.issuedDate ? (po.issuedDate | date: 'dd/MM/yyyy') : '—' }} · {{ poItemCount(po) }} {{ 'ApDocument_Items' | translateWF: 'items' }}
                            </span>
                          </div>
                          <span class="po-list__total">{{ poTotal(po) | number: '1.2-2' }} {{ currencyLabel(po.currency) }}</span>
                          @if (isPoAdded(po.id)) {
                            <span class="po-list__added"><i class="pi pi-check"></i> {{ 'ApDocument_Added' | translateWF: 'Added' }}</span>
                          } @else {
                            <a class="po-list__add" (click)="addPurchaseOrder(po)"><i class="pi pi-plus"></i> {{ 'ApDocument_AddItems' | translateWF: 'Add items' }}</a>
                          }
                        </div>
                      } @empty {
                        <div class="po-list__empty">
                          {{
                            'ApDocument_NoOpenPos'
                              | translateWF
                                : 'No open purchase orders for this
                  provider.'
                          }}
                        </div>
                      }
                    </div>

                    @if (poTotalRecords() > poPageSize) {
                      <p-paginator
                        [first]="poFirst()"
                        [rows]="poPageSize"
                        [totalRecords]="poTotalRecords()"
                        (onPageChange)="onPoPageChange($event)"
                        styleClass="po-picker__paginator"
                      />
                    }
                  }
                </div>
              } @else {
                <div class="text-sm text-surface-500">
                  {{
                    'ApDocument_SelectProviderFirst'
                      | translateWF
                        : 'Select a provider
              first to add purchase orders.'
                  }}
                </div>
              }
            </p-card>
          </p-panel>
        }

        <!-- Line items -->
        <p-panel [toggleable]="true">
          <ng-template pTemplate="header">
            <div class="row-between-center w-full">
              <span class="font-semibold">{{ 'ApDocument_Section_Lines' | translateWF: 'Line items' }} <span class="text-red-500">*</span></span>
              <div class="row-start-center gap-3">
                <!-- #4727 — re-hydrate every item line's master so the per-line GL accounts refresh, without
                     clearing selections. Only for item-capable draft documents. -->
                @if (isDraft() && hasItemLines()) {
                  <p-button
                    [label]="'ApDocument_ReloadItems' | translateWF: 'Reload items'"
                    icon="pi pi-refresh"
                    size="small"
                    variant="outlined"
                    severity="secondary"
                    (click)="reloadAllItems()"
                    [pTooltip]="'ApDocument_ReloadItems_Tip' | translateWF: 'Re-fetch every item master (refreshes the GL accounts on each line) without clearing selections.'"
                  />
                }
                <span class="text-sm text-surface-500">{{
                  'ApDocument_AtLeastOneLine'
                    | translateWF
                      : 'At least one line
                  required'
                }}</span>
              </div>
            </div>
          </ng-template>

          <p-card data-variant="compact">
            <!-- Pre-Invoice (#4306): lines come from the source invoice — no PO picker. Covers both the create
               flow (usePreInvoice) and a loaded document with a saved PreInvoiceReference (isPreInvoice). -->
            @if (isPreInvoice()) {
              <div class="text-sm text-surface-500 mb-2">
                {{
                  'ApDocument_PreInvoice_LinesHint'
                    | translateWF
                      : 'Lines are filled from the pre-invoice — set a GL
              account per line.'
                }}
              </div>
            } @else if (isDraft() && !isNote() && !useItemTypeAhead()) {
              <!-- Non-item document types: free manual lines. Receipt / Purchase Receipt also let the user
                 search the item master and add an item-based line. Notes mirror the source invoice's lines,
                 so no add-line here (the user only enters an amount per line). -->
              <div class="mb-3 flex flex-wrap items-center gap-2">
                @if (allowItemSearch()) {
                  <p-autoComplete
                    [formControl]="lineItemSearch"
                    #itemSearch="cslServerSearch"
                    [cslServerSearch]="fetchItems"
                    [suggestions]="itemSearch.results()"
                    (onSelect)="addLineFromItem($event.value)"
                    optionLabel="itemCode"
                    [forceSelection]="true"
                    [dropdown]="true"
                    dropdownIcon="pi pi-search"
                    [minLength]="1"
                    appendTo="body"
                    [placeholder]="'ApDocument_SearchItem' | translateWF: 'Search an item to add…'"
                    styleClass="min-w-[22rem]"
                  >
                    <ng-template let-item pTemplate="item">
                      <div class="col">
                        <span
                          ><strong>{{ getStrictItem(item).itemCode }}</strong> — {{ itemLongName(getStrictItem(item)) }}</span
                        >
                      </div>
                    </ng-template>
                  </p-autoComplete>
                }
                <p-button [label]="'ApDocument_AddLine' | translateWF: 'Add line'" icon="pi pi-plus" size="small" variant="outlined" (click)="addLine()" />
              </div>
            }

            @if (isNote()) {
              <!-- #6324 — a note's lines: the original line it adjusts, then the reason's inputs. Totals preview what
                   the API derives; only the lines checked are sent (#7052). Financiera lines are typed from scratch. -->
              @if (noteReason() === null) {
                <div class="po-group__empty">{{ 'ApDocument_Note_ChooseReasonFirst' | translateWF: 'Choose the reason to see the lines.' }}</div>
              } @else {
                @if (isFinancialNote() && isDraft()) {
                  <div class="mb-3">
                    <p-button [label]="'ApDocument_AddLine' | translateWF: 'Add line'" icon="pi pi-plus" size="small" variant="outlined" (click)="addLine()" />
                  </div>
                }
                @for (group of poGroups(); track group.reference) {
                  <!-- #7051 — grouped per PO like an invoice's lines, so the same item on two POs reads as two lines.
                       Lines with no PO (charges, initial-load Receipts) get a header only when POs sit beside them. -->
                  <div class="po-group">
                    @if (showNotePoGroupHeader(group)) {
                      <div class="po-group__header">
                        <span class="po-group__title">
                          @if (group.reference) {
                            <i class="pi pi-shopping-cart"></i> {{ 'ApDocument_Note_PurchaseOrder' | translateWF: 'PO' }}
                            <strong style="margin-left: 10px"> {{ group.number || '—' }}</strong>
                          } @else {
                            <i class="pi pi-file"></i> <strong>{{ 'ApDocument_Note_NoPurchaseOrder' | translateWF: 'No purchase order' }}</strong>
                          }
                          · {{ group.lines.length }} {{ 'ApDocument_LinesLower' | translateWF: 'lines' }}
                        </span>
                      </div>
                    }
                    <p-table
                      [value]="group.lines"
                      [scrollable]="true"
                      [rowHover]="true"
                      styleClass="cs-datatable-cell-top cs-datatable-cell-fill cs-datatable-numeric-right"
                      [tableStyle]="{ 'min-width': '60rem' }"
                    >
                      <ng-template pTemplate="header">
                        <tr>
                          @if (isFinancialNote()) {
                            <th>{{ 'ApDocument_Line_Description' | translateWF: 'Description' }}</th>
                            <th style="width: 22%">{{ 'ApDocument_Line_GlAccount' | translateWF: 'GL Account' }}</th>
                            <th style="width: 14%">{{ 'ApDocument_Line_Classification' | translateWF: 'Classification' }}</th>
                            <th style="width: 14%" class="text-right">{{ 'ApDocument_Note_AmountBeforeIsv' | translateWF: 'Amount before ISV' }}</th>
                            <th style="width: 8%" class="text-center">{{ 'ApDocument_Note_BringsIsv' | translateWF: 'Brings ISV' }}</th>
                          } @else {
                            <!-- #7052 — each original line is picked on purpose; there is no select-all. -->
                            @if (isDraft()) {
                              <th style="width: 5rem" class="text-center">{{ 'ApDocument_Note_AdjustLine' | translateWF: 'Adjust' }}</th>
                            }
                            <th>{{ 'ApDocument_Note_OriginalLine' | translateWF: 'Original line' }}</th>
                            @switch (noteReason()) {
                              @case (ApNoteReason.Quantity) {
                                <th style="width: 16%" class="text-right">
                                  {{
                                    isCreditNote()
                                      ? ('ApDocument_Note_UnitsToCredit' | translateWF: 'Units to credit')
                                      : ('ApDocument_Note_UnitsToCharge' | translateWF: 'Units to charge')
                                  }}
                                </th>
                              }
                              @case (ApNoteReason.PriceOrValue) {
                                <th style="width: 14%">{{ 'ApDocument_Note_PriceMode' | translateWF: 'Stated as' }}</th>
                                <th style="width: 14%" class="text-right">{{ 'ApDocument_Note_Value' | translateWF: 'Correct price / amount' }}</th>
                                <th style="width: 10%" class="text-right">{{ 'ApDocument_Note_Units' | translateWF: 'Units' }}</th>
                                <th style="width: 8%" class="text-center">{{ 'ApDocument_Note_AdjustsIsv' | translateWF: 'Adjusts ISV' }}</th>
                              }
                              @case (ApNoteReason.Tax) {
                                <th style="width: 16%">{{ 'ApDocument_Note_CorrectRate' | translateWF: 'Correct ISV rate' }}</th>
                              }
                            }
                            <th style="width: 14%">{{ 'ApDocument_Line_GlAccount' | translateWF: 'GL Account' }}</th>
                          }
                          <th style="width: 10%" class="text-right">{{ 'ApDocument_Taxes' | translateWF: 'Taxes' }}</th>
                          <th style="width: 12%" class="text-right">{{ 'ApDocument_Line_Total' | translateWF: 'Total' }}</th>
                          @if (isFinancialNote()) {
                            <th style="width: 3rem"></th>
                          }
                        </tr>
                      </ng-template>
                      <ng-template pTemplate="body" let-row>
                        @let line = getLineGroup(row);
                        @let preview = notePreview(line);
                        <tr [formGroup]="line" [class.opacity-60]="!isFinancialNote() && !preview && !isDraft()">
                          @if (isFinancialNote()) {
                            <td>
                              <input
                                type="text"
                                formControlName="description"
                                pInputText
                                class="w-full"
                                [readonly]="!isDraft()"
                                [ngClass]="{ 'ng-invalid ng-dirty': line.controls.description.invalid && line.controls.description.touched }"
                              />
                            </td>
                            <td>
                              <app-financial-account-select
                                [referenceControl]="line.controls.glAccountReference"
                                [numberControl]="line.controls.glAccountNumber"
                                [nameControl]="line.controls.glAccountName"
                                [postingOnly]="true"
                                [required]="true"
                                [readonly]="!isDraft()"
                              />
                            </td>
                            <td>
                              <p-select
                                formControlName="itemClassificationCode"
                                [options]="itemClassifications()"
                                optionLabel="name"
                                optionValue="code"
                                [filter]="true"
                                filterBy="name,code"
                                [readonly]="!isDraft()"
                                [placeholder]="'ApDocument_Line_Classification_Select' | translateWF: 'Select'"
                                (onChange)="onLineClassificationChanged()"
                                appendTo="body"
                                styleClass="w-full"
                                [ngClass]="{
                                  'ng-invalid ng-dirty': line.controls.itemClassificationCode.invalid && line.controls.itemClassificationCode.touched,
                                }"
                              />
                            </td>
                            <td class="text-right">
                              <p-inputNumber
                                formControlName="noteAmount"
                                mode="decimal"
                                [minFractionDigits]="2"
                                [maxFractionDigits]="2"
                                [min]="0"
                                [readonly]="!isDraft()"
                                styleClass="w-full"
                              />
                            </td>
                            <td class="text-center"><p-checkbox formControlName="noteAdjustsTax" [binary]="true" [readonly]="!isDraft()" /></td>
                          } @else {
                            @if (isDraft()) {
                              <td class="text-center">
                                <p-checkbox
                                  formControlName="noteSelected"
                                  [binary]="true"
                                  [pTooltip]="'ApDocument_Note_AdjustLine_Tip' | translateWF: 'Check to adjust this line on the note'"
                                  (onChange)="onNoteLineToggled(line)"
                                />
                              </td>
                            }
                            <td>
                              <div class="font-medium">{{ line.controls.sourceLineNumber.value }} · {{ line.controls.description.value }}</div>
                              <small class="text-surface-500 tabular-nums">
                                {{ line.controls.sourceQuantity.value | number: '1.0-4' }} {{ getUnitName(line.controls.uom.value) }} ×
                                {{ sourceNetUnitPrice(line) | number: '1.2-4' }} · ISV {{ line.controls.sourceTaxRate.value * 100 | number: '1.0-2' }} %
                              </small>
                            </td>
                            @switch (noteReason()) {
                              @case (ApNoteReason.Quantity) {
                                <td class="text-right">
                                  <p-inputNumber
                                    formControlName="noteQuantity"
                                    mode="decimal"
                                    [maxFractionDigits]="4"
                                    [min]="0"
                                    [readonly]="!isDraft()"
                                    styleClass="w-full"
                                    [ngClass]="{ 'ng-invalid ng-dirty': line.controls.noteQuantity.invalid }"
                                  />
                                  @if (line.controls.noteQuantity.hasError('exceedsSource')) {
                                    <small class="block text-red-500 mt-1">
                                      {{ 'ApDocument_Note_UnitsExceeded' | translateWF: 'More than the units invoiced:' }}
                                      {{ line.controls.sourceQuantity.value | number: '1.0-4' }}
                                    </small>
                                  }
                                </td>
                              }
                              @case (ApNoteReason.PriceOrValue) {
                                <td>
                                  <p-select
                                    formControlName="notePriceMode"
                                    [options]="notePriceModeOptions()"
                                    optionLabel="label"
                                    optionValue="value"
                                    [readonly]="!isDraft()"
                                    appendTo="body"
                                    styleClass="w-full"
                                  />
                                </td>
                                <td class="text-right">
                                  @if (line.controls.notePriceMode.value === ApNotePriceMode.FixedAmount) {
                                    <p-inputNumber
                                      formControlName="noteAmount"
                                      mode="decimal"
                                      [minFractionDigits]="2"
                                      [maxFractionDigits]="2"
                                      [min]="0"
                                      [readonly]="!isDraft()"
                                      styleClass="w-full"
                                    />
                                  } @else {
                                    <p-inputNumber
                                      formControlName="noteCorrectUnitPrice"
                                      mode="decimal"
                                      [minFractionDigits]="2"
                                      [maxFractionDigits]="4"
                                      [min]="0"
                                      [readonly]="!isDraft()"
                                      styleClass="w-full"
                                    />
                                  }
                                </td>
                                <td class="text-right">
                                  @if (line.controls.notePriceMode.value === ApNotePriceMode.CorrectUnitPrice) {
                                    <p-inputNumber
                                      formControlName="noteQuantity"
                                      mode="decimal"
                                      [maxFractionDigits]="4"
                                      [min]="0"
                                      [placeholder]="(line.controls.sourceQuantity.value | number: '1.0-4') ?? ''"
                                      [readonly]="!isDraft()"
                                      styleClass="w-full"
                                      [ngClass]="{ 'ng-invalid ng-dirty': line.controls.noteQuantity.invalid }"
                                    />
                                    @if (line.controls.noteQuantity.hasError('exceedsSource')) {
                                      <small class="block text-red-500 mt-1">
                                        {{ 'ApDocument_Note_UnitsExceeded' | translateWF: 'More than the units invoiced:' }}
                                        {{ line.controls.sourceQuantity.value | number: '1.0-4' }}
                                      </small>
                                    }
                                  } @else {
                                    <span class="text-surface-400">—</span>
                                  }
                                </td>
                                <td class="text-center"><p-checkbox formControlName="noteAdjustsTax" [binary]="true" [readonly]="!isDraft()" /></td>
                              }
                              @case (ApNoteReason.Tax) {
                                <td>
                                  <p-select
                                    formControlName="noteCorrectTaxRate"
                                    [options]="noteTaxRateOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    [showClear]="true"
                                    [readonly]="!isDraft()"
                                    appendTo="body"
                                    styleClass="w-full"
                                  />
                                </td>
                              }
                            }
                            <!-- The original line's account, fixed (#6324): the API takes it from there whatever is sent. The
                             field waits for the name (loadNoteLineAccountNames), or it would fetch it once per line. -->
                            <td>
                              @if (line.controls.glAccountName.value) {
                                <app-financial-account-select
                                  [referenceControl]="line.controls.glAccountReference"
                                  [numberControl]="line.controls.glAccountNumber"
                                  [nameControl]="line.controls.glAccountName"
                                  [readonly]="true"
                                />
                              } @else {
                                <span class="tabular-nums text-surface-600">{{ line.controls.glAccountNumber.value || '—' }}</span>
                              }
                            </td>
                          }
                          <td class="text-right tabular-nums">{{ preview ? (preview.taxAmount | number: '1.2-2') : '—' }}</td>
                          <td class="text-right tabular-nums font-medium">{{ preview ? (preview.lineTotal | number: '1.2-2') : '—' }}</td>
                          @if (isFinancialNote()) {
                            <td class="text-center">
                              @if (isDraft()) {
                                <p-button icon="pi pi-trash" severity="danger" variant="text" size="small" (click)="removeLine(row.index)" />
                              }
                            </td>
                          }
                        </tr>
                      </ng-template>
                    </p-table>
                  </div>
                } @empty {
                  <div class="po-group__empty">
                    {{
                      isFinancialNote()
                        ? ('ApDocument_NoLines' | translateWF: 'No lines added')
                        : ('ApDocument_Note_PickOriginal' | translateWF: 'Pick the original invoice or receipt to load its lines.')
                    }}
                  </div>
                }
              }
            }
            <!-- Lines grouped per PO (one group for non-item types). -->
            <!-- Notes render their own table above (#6324). -->
            @for (group of isNote() ? [] : poGroups(); track group.reference) {
              <div class="po-group">
                <!-- #4767 — a receipt-sourced pre-invoice has no PO, so its single group renders headerless. -->
                @if (useItemTypeAhead() && group.reference) {
                  <div class="po-group__header">
                    <span class="po-group__title">
                      <i class="pi pi-shopping-cart"></i> <strong>{{ group.number }}</strong> · {{ group.lines.length }} {{ 'ApDocument_LinesLower' | translateWF: 'lines' }} ·
                      {{ 'ApDocument_PricesLocked' | translateWF: 'prices locked to PO' }}
                    </span>
                    @if (isDraft() && !isPreInvoice()) {
                      <div class="row-sm-start-center">
                        <p-button
                          [label]="'ApDocument_RestorePoLines' | translateWF: 'Restore removed lines'"
                          icon="pi pi-refresh"
                          size="small"
                          variant="text"
                          severity="secondary"
                          [pTooltip]="'ApDocument_RestorePoLines_Tip' | translateWF: 'Adds back this PO\'s removed lines. Lines already in the list keep their values.'"
                          tooltipPosition="top"
                          (click)="resetPurchaseOrderLines(group.reference)"
                        />
                        <p-button
                          [label]="'ApDocument_RemovePo' | translateWF: 'Remove PO'"
                          icon="pi pi-times"
                          size="small"
                          variant="outlined"
                          severity="secondary"
                          (click)="removePurchaseOrder(group.reference)"
                        />
                      </div>
                    }
                  </div>
                }

                <!-- Scrollable so the columns can keep real minimum widths instead of being squeezed by
                   percentages. No frozen columns: pinning Total right meant the scrolling cells passed
                   underneath it, and a control caught on the boundary (the Qty input group) rendered as a
                   sliced, half-drawn input. Plain horizontal scroll keeps every cell whole. -->
                <!-- Each PO group renders its own p-table, so every column carries a matching width +
                   min-width to keep the groups lined up with each other. NOT table-layout: fixed — these
                   cells hold form controls with intrinsic widths, and fixed layout makes them overflow into
                   the next column instead of the column growing. The controls are forced to 100% of their
                   cell in SCSS so nothing pushes a column wider than declared. -->
                <p-table
                  [value]="group.lines"
                  [rowHover]="true"
                  [scrollable]="true"
                  [tableStyle]="{ 'min-width': '70rem' }"
                  styleClass="cs-datatable-cell-top cs-datatable-cell-fill cs-datatable-numeric-right"
                >
                  <ng-template pTemplate="header">
                    <tr>
                      <th style="width: 20rem; min-width: 20rem">{{ 'ApDocument_Line_Item' | translateWF: 'Item' }}</th>
                      <th style="width: 16rem; min-width: 16rem">
                        {{
                          'ApDocument_Line_GlAccount'
                            | translateWF
                              : 'GL
                      Account'
                        }}
                      </th>
                      <!-- #4842 — goods vs services. Read-only for an item line (it comes from the item master);
                         a required pick for a description-only line, which has no item to classify. -->
                      <th style="width: 13rem; min-width: 13rem">
                        {{ 'ApDocument_Line_Classification' | translateWF: 'Classification' }}
                      </th>
                      <!-- Amount-only kinds (#4462): description + amount — no qty, no redundant line total. -->
                      @if (!isAmountOnly()) {
                        <th style="width: 11rem; min-width: 11rem" class="text-right">{{ 'ApDocument_Line_Qty' | translateWF: 'Qty' }}</th>
                      }
                      <th style="width: 11rem; min-width: 11rem" class="text-right">
                        @if (isAmountOnly()) {
                          {{ 'ApDocument_Line_Amount' | translateWF: 'Amount' }}
                        } @else {
                          {{ 'ApDocument_Line_UnitPrice' | translateWF: 'Unit Price' }}
                        }
                      </th>
                      @if (!isAmountOnly()) {
                        <th style="width: 9rem; min-width: 9rem" class="text-right">
                          {{ 'ApDocument_Line_Total' | translateWF: 'Total' }}
                        </th>
                      }
                      <th style="width: 3.5rem; min-width: 3.5rem"></th>
                    </tr>
                  </ng-template>

                  <ng-template pTemplate="body" let-row>
                    @let line = getLineGroup(row);
                    <tr [formGroup]="line">
                      <!-- Item = description as the primary value with the item code beneath it, mirroring how
                         the GL account shows its name over its number. -->
                      <td>
                        @if (line.controls.itemSelection.value) {
                          <span class="ap-line-text">{{ line.controls.description.value }}</span>
                        } @else {
                          <input
                            type="text"
                            formControlName="description"
                            pInputText
                            class="w-full"
                            [ngClass]="{ 'ng-invalid ng-dirty': line.controls.description.invalid && line.controls.description.touched }"
                          />
                        }
                        @if (line.controls.itemSelection.value?.itemCode; as itemCode) {
                          <small class="text-surface-500 block mt-1">{{ itemCode }}</small>
                        }
                      </td>
                      <td>
                        @if (lineItemMissingGl(line)) {
                          <span class="ap-line-text inline-flex items-center gap-1 text-sm text-surface-500">
                            <i
                              class="pi pi-info-circle text-amber-500"
                              [pTooltip]="'ApDocument_Line_NoItemGl' | translateWF: 'This item has no GL accounts — set them in the item master.'"
                            ></i>
                            {{ 'ApDocument_Line_NoItemGl_Short' | translateWF: 'No GL accounts on item' }}
                          </span>
                        } @else {
                          <!-- A goods (Bien) item posts to exactly one account, so the line takes it from the
                             item master and shows it read-only; a service line picks freely from the whole
                             chart of accounts (allowedAccountNumbers is null for it). -->
                          <app-financial-account-select
                            [referenceControl]="line.controls.glAccountReference"
                            [numberControl]="line.controls.glAccountNumber"
                            [nameControl]="line.controls.glAccountName"
                            [postingOnly]="true"
                            [allowedAccountNumbers]="lineGlAccountNumbers(line)"
                            [required]="true"
                            [readonly]="!isDraft() || isNote() || lineGlAccountLocked(line)"
                          />
                          @if (isDraft() && !isNote() && lineGlAccountLocked(line)) {
                            <!-- A field that refuses to open reads as broken unless it says who owns it. The
                               unclassified case is locked for a DIFFERENT reason — nothing came from the
                               master, the account simply cannot be decided yet — so it says so rather than
                               claiming a source it does not have. -->
                            @if (lineMissingMasterClassification(line)) {
                              <small class="block mt-1 text-xs text-amber-600">
                                {{ 'ApDocument_Line_GlNeedsClassification' | translateWF: 'Set once the item is classified' }}
                              </small>
                            } @else {
                              <small class="block mt-1 text-xs text-surface-500">
                                {{ 'ApDocument_Line_GlFromItem' | translateWF: 'From the item master' }}
                              </small>
                            }
                          }
                        }
                      </td>
                      <!-- #4842 — an item line shows the master's classification as plain text (the control is
                         disabled, and the API re-asserts it from the master on save regardless); a
                         description-only line gets the picker, and it is required. -->
                      <td>
                        @if (line.controls.itemClassificationCode.disabled) {
                          @if (lineMissingMasterClassification(line)) {
                            <!-- #4842 — the item master has no classification. Not shown as "—": that reads as
                               "not applicable" when it actually blocks the save and, left blank, makes the
                               line count as a service and demand a Constancia. A picker here would be
                               overwritten by the API from the master, so the message names who can fix it. -->
                            <span class="ap-line-text flex items-start gap-1 text-amber-600">
                              <i class="pi pi-exclamation-triangle text-xs mt-1 text-amber-500"></i>
                              <span>{{
                                'ApDocument_Line_Classification_AskDataMaster'
                                  | translateWF
                                    : 'Not classified — ask
                          Data Master to assign one'
                              }}</span>
                            </span>
                          } @else {
                            <span class="ap-line-text">
                              {{ line.controls.itemClassificationCode.value || ('ApDocument_Line_Classification_None' | translateWF: '—') }}
                            </span>
                          }
                        } @else {
                          <p-select
                            formControlName="itemClassificationCode"
                            [options]="itemClassifications()"
                            optionLabel="name"
                            optionValue="code"
                            [filter]="true"
                            filterBy="name,code"
                            appendTo="body"
                            styleClass="w-full"
                            [readonly]="!isDraft()"
                            [placeholder]="'ApDocument_Line_Classification_Select' | translateWF: 'Select'"
                            (onChange)="onLineClassificationChanged()"
                            [ngClass]="{
                              'ng-invalid ng-dirty': line.controls.itemClassificationCode.invalid && line.controls.itemClassificationCode.touched,
                            }"
                          />
                        }
                      </td>
                      @if (!isAmountOnly()) {
                        <td class="text-right">
                          <!-- #4724 — the invoiced-vs-ordered indicator sits top-right, fixed, so rows stay aligned;
                             hover it for the ordered / already-invoiced / remaining breakdown. Turns red when this
                             line's qty exceeds what's left to invoice (also hard-blocked on save + server-side). -->
                          <div class="flex items-start gap-1.5">
                            <div class="flex-1 min-w-0">
                              <p-inputGroup>
                                <p-inputNumber formControlName="quantity" mode="decimal" [minFractionDigits]="0" [maxFractionDigits]="4" styleClass="w-full" />
                                @if (getUnitName(line.controls.uom.value)) {
                                  <p-inputGroupAddon>{{ getUnitName(line.controls.uom.value) }}</p-inputGroupAddon>
                                }
                              </p-inputGroup>
                            </div>
                            @if (lineInvoicedInfo(line); as info) {
                              @if (info.invoicedQuantity > 0 || lineOverInvoiced(line)) {
                                <i
                                  class="pi mt-[0.65rem] shrink-0 cursor-help"
                                  [ngClass]="lineOverInvoiced(line) ? 'pi-exclamation-triangle text-red-500' : 'pi-info-circle text-surface-400'"
                                  [pTooltip]="lineInvoicedTooltip(line)"
                                  tooltipPosition="left"
                                ></i>
                              }
                            }
                          </div>
                        </td>
                      }
                      <td class="text-right">
                        <p-inputGroup>
                          @if (line.controls.purchaseOrderReference.value) {
                            <p-inputGroupAddon><i class="pi pi-lock text-surface-400"></i></p-inputGroupAddon>
                          }
                          <p-inputNumber formControlName="unitPrice" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="4" styleClass="w-full" />
                        </p-inputGroup>
                        <!-- #4723 — taxable PO item: the unit price folds in the tax (tax-inclusive TotalPrice ÷ qty)
                           so the total already reflects it; the line still posts as exempt. -->
                        @if (lineIncludesTax(line)) {
                          <small class="text-surface-500 row-end-center gap-1 mt-1">
                            <i
                              class="pi pi-info-circle text-surface-400"
                              [pTooltip]="
                                'ApDocument_Line_TaxInclusive_Tip'
                                  | translateWF: 'Unit price includes the PO item tax, so the total already reflects it. The line still posts as tax-exempt.'
                              "
                            ></i>
                            {{ lineTaxRatePercent(line) }}% {{ 'ApDocument_Line_TaxIncluded' | translateWF: 'tax incl.' }}
                          </small>
                        }
                      </td>
                      @if (!isAmountOnly()) {
                        <td class="text-right">
                          <!-- Total and unit price work together off the quantity: editing the total back-solves
                             the unit price (total ÷ qty). Locked (PO-sourced) lines stay read-only. -->
                          @if (line.controls.unitPrice.disabled) {
                            <span class="ap-line-text font-medium">{{ lineTotal(line) | number: '1.2-2' }}</span>
                          } @else {
                            <p-inputNumber
                              [ngModel]="lineTotal(line)"
                              [ngModelOptions]="{ standalone: true }"
                              (ngModelChange)="onLineTotalChange(line, $event)"
                              mode="decimal"
                              [minFractionDigits]="2"
                              [maxFractionDigits]="2"
                              styleClass="w-full"
                            />
                          }
                        </td>
                      }
                      <td class="text-center">
                        @if (isDraft() && !isPreInvoice()) {
                          <p-button icon="pi pi-trash" severity="danger" variant="text" size="small" (click)="removeLine(row.index)" />
                        }
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              </div>
            } @empty {
              @if (!isNote()) {
                <div class="po-group__empty">{{ 'ApDocument_NoLines' | translateWF: 'No lines added' }}</div>
              }
            }

            <!-- #4725 — Charges / Fees: general non-PO lines (bank fee, other fees the provider charges you).
               They add to the document total and are payable to the provider. Shown only on PO-driven
               Invoices, including one registered from an Order Invoice (#6537); free-line document types add
               such lines directly above, and a receipt-sourced pre-invoice has none. -->
            @if (allowsCharges() && selectedProvider()) {
              <div class="charges-section mt-4">
                <div class="row-between-center mb-2">
                  <span class="font-semibold text-sm"> <i class="pi pi-receipt"></i> {{ 'ApDocument_Section_Charges' | translateWF: 'Charges / Fees' }} </span>
                  @if (isDraft()) {
                    <p-button [label]="'ApDocument_AddCharge' | translateWF: 'Add charge'" icon="pi pi-plus" size="small" variant="outlined" (click)="addChargeLine()" />
                  }
                </div>
                @if (chargeLines().length) {
                  <p-table
                    [value]="chargeLines()"
                    [rowHover]="true"
                    styleClass="cs-datatable-cell-top cs-datatable-cell-fill cs-datatable-numeric-right"
                    [tableStyle]="{ 'min-width': '40rem' }"
                  >
                    <ng-template pTemplate="header">
                      <tr>
                        <th>{{ 'ApDocument_Line_Description' | translateWF: 'Description' }}</th>
                        <th style="width: 24%">{{ 'ApDocument_Line_GlAccount' | translateWF: 'GL Account' }}</th>
                        <th style="width: 18%">{{ 'ApDocument_Line_Classification' | translateWF: 'Classification' }}</th>
                        <th style="width: 18%" class="text-right">{{ 'ApDocument_Line_Amount' | translateWF: 'Amount' }}</th>
                        <th style="width: 3rem"></th>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                      @let line = getLineGroup(row);
                      <tr [formGroup]="line">
                        <td>
                          <input
                            type="text"
                            formControlName="description"
                            pInputText
                            class="w-full"
                            [placeholder]="'ApDocument_Charge_DescriptionHint' | translateWF: 'e.g. Bank fee'"
                            [ngClass]="{ 'ng-invalid ng-dirty': line.controls.description.invalid && line.controls.description.touched }"
                          />
                        </td>
                        <td>
                          <app-financial-account-select
                            [referenceControl]="line.controls.glAccountReference"
                            [numberControl]="line.controls.glAccountNumber"
                            [nameControl]="line.controls.glAccountName"
                            [postingOnly]="true"
                            [required]="true"
                            [readonly]="!isDraft()"
                          />
                        </td>
                        <!-- #4842 — a charge has no item, so the clerk classifies it; the control is required on
                           every description-only line, and without a picker here a charge could never be saved. -->
                        <td>
                          <p-select
                            formControlName="itemClassificationCode"
                            [options]="itemClassifications()"
                            optionLabel="name"
                            optionValue="code"
                            [filter]="true"
                            filterBy="name,code"
                            appendTo="body"
                            styleClass="w-full"
                            [readonly]="!isDraft()"
                            [placeholder]="'ApDocument_Line_Classification_Select' | translateWF: 'Select'"
                            (onChange)="onLineClassificationChanged()"
                            [ngClass]="{
                              'ng-invalid ng-dirty': line.controls.itemClassificationCode.invalid && line.controls.itemClassificationCode.touched,
                            }"
                          />
                        </td>
                        <td class="text-right">
                          <p-inputNumber formControlName="unitPrice" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2" styleClass="w-full" />
                        </td>
                        <td class="text-center">
                          @if (isDraft()) {
                            <p-button icon="pi pi-trash" severity="danger" variant="text" size="small" (click)="removeLine(row.index)" />
                          }
                        </td>
                      </tr>
                    </ng-template>
                  </p-table>
                } @else {
                  <div class="text-sm text-surface-500">{{ 'ApDocument_NoCharges' | translateWF: 'No charges or fees added.' }}</div>
                }
              </div>
            }
          </p-card>
        </p-panel>

        <!-- Notes -->
        <p-panel [header]="'ApDocument_Section_Notes' | translateWF: 'Notes'" [toggleable]="true" [collapsed]="true">
          <p-card>
            <div class="form-box">
              <textarea pTextarea formControlName="notes" rows="3" class="w-full"></textarea>
            </div>
          </p-card>
        </p-panel>

        <!-- Attachments — the scanned invoice and anything else that supports the document. Not gated on
             Draft: the scan usually arrives after posting, and a file changes nothing in the ledger. It IS
             gated on the document existing, since a file has to be attached to something. -->
        <p-panel [toggleable]="true">
          <ng-template pTemplate="header">
            <div class="row-between-center w-full">
              <span class="font-semibold">
                {{ 'ApDocument_Section_Attachments' | translateWF: 'Attachments' }}
                @if (attachments().length) {
                  <span class="text-sm font-normal text-surface-500">({{ attachments().length }})</span>
                }
              </span>
              @if (canManageAttachments()) {
                <input #attachmentFile type="file" accept="application/pdf,image/*" class="hidden" (change)="onAttachmentSelected($event, attachmentFile)" />
                <p-button
                  [label]="'ApDocument_Attachment_Add' | translateWF: 'Attach file'"
                  icon="pi pi-paperclip"
                  size="small"
                  variant="outlined"
                  (click)="attachmentFile.click()"
                />
              }
            </div>
          </ng-template>

          <p-card>
            @if (!canManageAttachments()) {
              <div class="text-sm text-surface-500">
                {{
                  'ApDocument_Attachment_SaveFirst'
                    | translateWF
                      : 'Save the document first — a file has to be attached
              to something.'
                }}
              </div>
            } @else if (!attachments().length) {
              <div class="text-sm text-surface-500">
                {{ 'ApDocument_Attachment_None' | translateWF: 'No files attached yet.' }}
              </div>
            } @else {
              <div class="col divide-y divide-[var(--surface-border)]">
                @for (attachment of attachments(); track attachment.name) {
                  <div class="row-start-center gap-3 py-2">
                    <span class="pi pi-file text-surface-500"></span>
                    <a class="flex-1 cursor-pointer text-primary hover:underline break-all" (click)="openAttachment(attachment)">
                      {{ attachmentFileName(attachment) }}
                    </a>
                    <p-button
                      icon="pi pi-trash"
                      severity="danger"
                      [text]="true"
                      [rounded]="true"
                      [pTooltip]="'Remove' | translateWF: 'Remove'"
                      (click)="removeAttachment(attachment)"
                    />
                  </div>
                }
              </div>
            }
          </p-card>
        </p-panel>
      </div>

      <!-- RIGHT COLUMN -->
      <div class="span-12-12-3 col sticky-summary">
        <!-- #4785 — at-a-glance identity: the three facts a clerk re-checks constantly, restated read-only
             so they stay visible while scrolling the lines. -->
        <p-panel>
          <ng-template pTemplate="header">
            <div class="row-between-center w-full">
              <span class="font-semibold">{{ 'ApDocument_Section_ThisDocument' | translateWF: 'This document' }}</span>
              <span class="text-xs text-surface-400">{{ 'ApDocument_ReadOnly' | translateWF: 'Read only' }}</span>
            </div>
          </ng-template>
          <p-card>
            <div class="summary-rows">
              <div class="summary-rows__row">
                <span>{{ 'ApDocument_ProviderInvoiceNumber_Short' | translateWF: 'Document #' }}</span>
                <span>{{ form.controls.fiscalNumber.value || documentNumber() || '—' }}</span>
              </div>
              <div class="summary-rows__row">
                <span>{{ 'ApDocument_DocumentDate' | translateWF: 'Document date' }}</span>
                <span>{{ form.controls.documentDate.value ? (form.controls.documentDate.value | date: 'dd/MM/yyyy') : '—' }}</span>
              </div>
              @if (!isCreditNote()) {
                <div class="summary-rows__row">
                  <span>
                    {{ 'ApDocument_DueDate' | translateWF: 'Due date' }}
                    <small class="block text-surface-500">{{ effectiveCreditDays }} {{ 'ApDocument_Days' | translateWF: 'days' }} · {{ paymentTermSourceLabel }}</small>
                  </span>
                  <span>{{ form.controls.dueDate.value ? (form.controls.dueDate.value | date: 'dd/MM/yyyy') : '—' }}</span>
                </div>
              }
            </div>
          </p-card>
        </p-panel>

        <!-- Currency & exchange -->
        <p-panel [header]="'ApDocument_Section_Currency' | translateWF: 'Currency & Exchange'">
          <p-card>
            <div class="grid-12">
              <div class="span-12">
                <div class="form-box">
                  <label for="currency">{{ 'Currency' | translateWF: 'Currency' }}</label>
                  <p-select id="currency" formControlName="currency" [options]="currencyOptions()" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full" />
                </div>
              </div>
              <!-- #5221 — everything below is about converting to the books' currency, so a document already
                 in it has nothing to show: it converts at 1 by definition and its functional amount IS its
                 total. Only the currency itself stays. -->
              @if (showExchangeDetails()) {
                <!-- ONE rate row, whatever the rate's provenance. This was three stacked readouts — booked,
                   source, daily — which read as three competing rates when only ever one applies. The label
                   names which one this is; the date underneath names the day it is priced at; Override and
                   reload sit on the row rather than below it as a permanent field. -->
                <div class="span-12">
                  <div class="exchange-row">
                    <div class="flex flex-col">
                      <span class="exchange-row__label">{{ rateOriginLabel }}</span>
                      <!-- The whole point of showing this: the API prices a document at ITS OWN date, never
                         today's, so a backdated invoice does not convert at the rate on the screen's clock. -->
                      <small class="text-xs text-surface-400">
                        {{ 'ApDocument_Rate_AsOfDate' | translateWF: 'For document date' }}
                        {{ rateAsOfDate() | date: 'dd/MM/yyyy' }}
                      </small>
                    </div>
                    <div class="flex items-center gap-2">
                      @if (canOverrideRate()) {
                        <a
                          role="button"
                          tabindex="0"
                          class="ap-link"
                          (click)="rateOverrideOn() ? onCancelRateOverride() : onOverrideRate()"
                          (keydown.enter)="rateOverrideOn() ? onCancelRateOverride() : onOverrideRate()"
                          (keydown.space)="rateOverrideOn() ? onCancelRateOverride() : onOverrideRate()"
                          >{{ rateOverrideOn() ? ('ApDocument_Rate_CancelOverride' | translateWF: 'Cancel') : ('ApDocument_Rate_Override' | translateWF: 'Override') }}</a
                        >
                        <!-- Re-ask for the rate without losing the form. The case it exists for: the table has
                           nothing for this date, finance publishes it while the document is open, and the only
                           recourse used to be reloading the page and retyping everything. -->
                        <p-button
                          type="button"
                          icon="pi pi-refresh"
                          severity="secondary"
                          [text]="true"
                          [rounded]="true"
                          size="small"
                          [loading]="rateLoading()"
                          (onClick)="reloadRate()"
                          [pTooltip]="'ApDocument_Rate_Reload' | translateWF: 'Reload the rate for this date'"
                          tooltipPosition="top"
                        />
                      }
                      <span class="exchange-row__value">{{ effectiveRate() !== null ? (effectiveRate() | number: '1.2-10') : '—' }}</span>
                    </div>
                  </div>
                  <!-- Named rather than left as a dash: "no rate yet" and "rate of nothing" look identical in a
                     readout, and only one of them is fixed by asking finance and pressing reload. -->
                  @if (rateUnavailable() && !hasBookedRate() && sourceExchangeRate() === null) {
                    <small class="text-red-500 block mt-1">
                      {{ 'ApDocument_Rate_Missing' | translateWF: 'No rate on file for this date. Ask finance to publish it, then reload.' }}
                    </small>
                  }
                  <!-- #4785 — when registering from a PO / Receipt the source document's own rate is what the API
                     applies, not the day's rate. The currency and the rate both belong to the source. -->
                  @if (!hasBookedRate() && sourceExchangeRate() !== null) {
                    <small class="text-surface-500 block mt-1">
                      {{ 'ApDocument_SourceRate_Applies' | translateWF: 'This rate is used instead of the daily rate.' }}
                    </small>
                  }
                </div>
                <!-- The rate the person holding the document says it was struck at (#5135). Every source above
                   is an inference — from a PO, from the day — and this is the one case where somebody is
                   looking at the paper. Bounded because a slipped decimal books cleanly and is invisible
                   afterwards: 24.50 typed as 2.45 leaves the payable out by a factor of ten. -->
                @if (canOverrideRate() && rateOverrideOn()) {
                  <div class="span-12">
                    <div class="form-box col">
                      <label for="rateToUsd">
                        {{ 'ApDocument_RateOverride' | translateWF: 'Override rate' }}
                        <span class="text-surface-500">({{ currencyLabel(form.controls.currency.value) }} {{ 'ApDocument_PerUsd' | translateWF: 'per USD' }})</span>
                      </label>
                      <p-inputNumber
                        inputId="rateToUsd"
                        formControlName="rateToUsd"
                        mode="decimal"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="10"
                        [min]="0"
                        [showClear]="true"
                        styleClass="w-full"
                        [ngClass]="{ 'ng-invalid ng-dirty': rateOutOfBounds() }"
                      />
                      <!-- `as` binds only on the primary @if, so the band is destructured once and the two
                         states branch inside it. -->
                      @if (rateBounds(); as bounds) {
                        @if (rateOutOfBounds()) {
                          <small class="text-red-500">
                            {{ 'ApDocument_RateOverride_OutOfBand' | translateWF: 'Outside the expected range' }}
                            {{ bounds.min | number: '1.2-2' }}–{{ bounds.max | number: '1.2-2' }} —
                            {{ 'ApDocument_RateOverride_CheckDecimal' | translateWF: 'check the decimal point.' }}
                          </small>
                        } @else {
                          <small class="text-surface-500">
                            {{ 'ApDocument_RateOverride_Hint' | translateWF: 'Expected' }} {{ bounds.min | number: '1.2-2' }}–{{ bounds.max | number: '1.2-2' }}.
                            {{
                              'ApDocument_RateOverride_Hint2'
                                | translateWF
                                  : "Blank uses the source document, the purchase
                    order or the day's rate."
                            }}
                          </small>
                        }
                      }
                    </div>
                  </div>
                }
                <!-- #4785 — manual entry removed; the functional amount is always derived API-side from the
                   day's rate (total × rate), so this is a read-only echo of what will be posted. -->
                <div class="span-12">
                  <div class="exchange-row">
                    <span class="exchange-row__label">{{ 'ApDocument_FunctionalAmount' | translateWF: 'Functional amount' }}</span>
                    <span class="exchange-row__value">{{ functionalAmountDisplay !== null ? (functionalAmountDisplay | number: '1.2-2') : '—' }}</span>
                  </div>
                </div>
              }
            </div>
          </p-card>
        </p-panel>

        <!-- Withholding (#4454): the decision moved OUT of invoice registration into the separate Constancia
             de Retención screen (the Financial Officer posts it before payment). Nothing to enter here. -->

        <!-- Summary -->
        <p-panel [header]="'ApDocument_Section_Summary' | translateWF: 'Summary'">
          <p-card>
            <div class="summary-rows">
              <div class="summary-rows__row">
                <span>{{ 'ApDocument_Subtotal' | translateWF: 'Subtotal' }}</span>
                <span>{{ itemsSubtotal() | number: '1.2-2' }} {{ currencyLabel(form.controls.currency.value) }}</span>
              </div>
              <!-- #4725 charge/fee lines, broken out of the subtotal rather than added on top. Only the
                 PO-driven Invoice has them; the other kinds put every cost on an ordinary line. -->
              @if (useItemTypeAhead()) {
                <div class="summary-rows__row">
                  <span>{{ 'ApDocument_Fees' | translateWF: 'Fees' }}</span>
                  <span>{{ feesTotal() | number: '1.2-2' }} {{ currencyLabel(form.controls.currency.value) }}</span>
                </div>
              }
              <!-- #4723 — a taxable PO line carries its tax inside the unit price. Extracted here with the
                 API's own formula, so the figure shown is the one that posts. It does NOT add to the total:
                 it is already inside the line totals above. -->
              <div class="summary-rows__row">
                <span>
                  {{ 'ApDocument_Taxes' | translateWF: 'Taxes' }}
                  @if (taxTotal() > 0) {
                    <!-- Recoverable ISV is an asset, not part of what the goods cost — it posts to its own
                       account instead of the inventory/expense account the rest of the line goes to. -->
                    <i
                      class="pi pi-info-circle ml-1 text-xs text-surface-400"
                      [pTooltip]="
                        'ApDocument_Taxes_Recoverable' | translateWF: 'Recoverable ISV — posts to the tax credit account, not to the item\'s inventory or expense account.'
                      "
                      tooltipPosition="top"
                    ></i>
                  }
                </span>
                <span>{{ taxTotal() | number: '1.2-2' }} {{ currencyLabel(form.controls.currency.value) }}</span>
              </div>
              <div class="summary-rows__row summary-rows__row--total">
                <span>
                  @if (isCreditNote()) {
                    {{ 'ApDocument_Note_Credit' | translateWF: 'Credit (reduces payable)' }}
                  } @else if (isDebitNote()) {
                    {{ 'ApDocument_Note_Charge' | translateWF: 'Charge (adds to payable)' }}
                  } @else {
                    {{ 'ApDocument_Total' | translateWF: 'Total' }}
                  }
                </span>
                <span [class.text-red-500]="isCreditNote()">
                  @if (isNote()) {
                    <span>{{ summarySign() < 0 ? '−' : '+' }}</span>
                  }
                  {{ grandTotal() | number: '1.2-2' }} {{ currencyLabel(form.controls.currency.value) }}
                </span>
              </div>
              <!-- #6324 — what the note does to its original. A credit note settles what the original still owes
                   and leaves the rest as the provider's open credit; a debit note is its own payable. -->
              @if (isNote() && noteOriginal(); as original) {
                <div class="summary-rows__row text-sm text-surface-600">
                  <span>{{ 'ApDocument_Note_OriginalBalanceBefore' | translateWF: 'Balance of' }} {{ original.number }}</span>
                  <span class="tabular-nums">{{ original.balanceDue ?? 0 | number: '1.2-2' }}</span>
                </div>
              }
              @if (noteCreditSplit(); as split) {
                @if (!isFinancialNote()) {
                  <div class="summary-rows__row text-sm text-surface-600">
                    <span>{{ 'ApDocument_Note_AppliedToOriginal' | translateWF: 'Applied to the original' }}</span>
                    <span class="tabular-nums">− {{ split.applied | number: '1.2-2' }}</span>
                  </div>
                  <div class="summary-rows__row text-sm text-surface-600">
                    <span>{{ 'ApDocument_Note_OriginalBalanceAfter' | translateWF: 'Original balance after' }}</span>
                    <span class="tabular-nums">{{ (noteOriginal()?.balanceDue ?? 0) - split.applied | number: '1.2-2' }}</span>
                  </div>
                }
                @if (split.providerCredit > 0) {
                  <div class="summary-rows__row text-sm font-medium">
                    <span>{{ 'ApDocument_Note_ProviderCredit' | translateWF: 'Left as provider credit' }}</span>
                    <span class="tabular-nums">{{ split.providerCredit | number: '1.2-2' }}</span>
                  </div>
                }
              }
              @if (isDebitNote()) {
                <small class="text-surface-500 block mt-1">{{
                  'ApDocument_Note_DebitIsOwnPayable' | translateWF: "A new payable, due on the original's payment term counted from this note's date."
                }}</small>
              }
            </div>
          </p-card>
        </p-panel>

        <!-- Withholding requirement (#4842/#5166). A saved document shows what the API derived (and any
             override); an unsaved draft shows a live preview of what the API will derive on save. The copy
             deliberately does not say services are the criterion — goods are withheld too, and the clerk can
             require a certificate on any document. -->
        <!-- A credit note is never withheld (#6324): it reduces the base of the invoice it credits. -->
        @if (!isPersisted() && !isCreditNote()) {
          @if (willRequireWithholding()) {
            <p-message
              severity="warn"
              styleClass="w-full"
              [text]="
                'ApDocument_Withholding_WillBeRequired' | translateWF: 'This document will require a withholding certificate (Constancia de Retención) before it can be paid.'
              "
            />
          }
        }
        @if (isPersisted() && !isCreditNote()) {
          <p-panel [header]="'ApDocument_Section_Withholding' | translateWF: 'Withholding'">
            <p-card>
              @if (isWithholdingWaived()) {
                <p-message
                  severity="info"
                  styleClass="w-full"
                  [text]="
                    ('ApDocument_Withholding_Waived' | translateWF: 'No withholding certificate required — waived') +
                    ': ' +
                    (withholdingWaivedReason() ?? '') +
                    (withholdingWaivedBy() ? ' (' + withholdingWaivedBy() + ')' : '')
                  "
                />
                <!-- #5166 — a waiver is no longer a one-way door: the same control that requires withholding on
                   an underived document also un-waives one, since both are "assert a certificate IS needed". -->
                @if (canRequireWithholding()) {
                  <div class="row-end-stretch mt-3">
                    <p-button
                      severity="secondary"
                      [outlined]="true"
                      icon="pi pi-exclamation-circle"
                      [label]="'ApDocument_Withholding_Force' | translateWF: 'Withholding is required'"
                      (onClick)="openRequireWithholdingDialog()"
                    />
                  </div>
                }
              } @else if (withholdingNotRequired()) {
                <p-message
                  severity="info"
                  styleClass="w-full"
                  [text]="'ApDocument_Withholding_NotRequired' | translateWF: 'No withholding certificate required for this document.'"
                />
                <!-- #5166 — the false → true direction. Offered exactly here, where the derived rule has just
                   said "no certificate", because that is the judgement being overridden. -->
                @if (canRequireWithholding()) {
                  <div class="row-end-stretch mt-3">
                    <p-button
                      severity="secondary"
                      [outlined]="true"
                      icon="pi pi-exclamation-circle"
                      [label]="'ApDocument_Withholding_Force' | translateWF: 'Withholding is required'"
                      (onClick)="openRequireWithholdingDialog()"
                    />
                  </div>
                }
              } @else {
                @if (withholdingForced()) {
                  <!-- Distinguished from a derived requirement on purpose: this one is somebody's decision, and
                     the panel names who made it and why. -->
                  <p-message
                    severity="warn"
                    styleClass="w-full"
                    [text]="
                      ('ApDocument_Withholding_ForcedNotice' | translateWF: 'Withholding was required manually — every line on this document can be withheld') +
                      ': ' +
                      (withholdingForcedReason() ?? '') +
                      (withholdingForcedBy() ? ' (' + withholdingForcedBy() + ')' : '')
                    "
                  />
                } @else {
                  <p-message
                    severity="warn"
                    styleClass="w-full"
                    [text]="
                      'ApDocument_Withholding_Required' | translateWF: 'This document requires a posted withholding certificate (Constancia de Retención) before it can be paid.'
                    "
                  />
                }

                @if (withholdingLockedReason()) {
                  <p-message severity="info" styleClass="w-full mt-2" [text]="withholdingLockedReason()!" />
                }

                <!-- One control for the one direction available here: the requirement exists, so the only move
                   is to remove it — whether it was derived or forced. -->
                @if (canWaiveWithholding()) {
                  <div class="row-end-stretch mt-3">
                    <p-button
                      severity="secondary"
                      [outlined]="true"
                      icon="pi pi-ban"
                      [label]="'ApDocument_Withholding_Waive' | translateWF: 'No withholding needed'"
                      (onClick)="openWaiveDialog()"
                    />
                  </div>
                }
              }
            </p-card>
          </p-panel>
        }

        <!-- Payable resumen (#4516): notes (credit/debit) + withholding + advances-per-PO + net for a posted
             document, so its full adjusted state is visible without opening the database. -->
        @if (showResumen()) {
          <p-panel [header]="'ApDocument_Section_Resumen' | translateWF: 'Payable summary'">
            <app-payable-document-summary [payable]="payableDetail()!" [currencyLabel]="currencyLabel(form.controls.currency.value)" />
          </p-panel>
        }

        <!-- Accounting (#5024 #5031): the AP control account and journal entry number are this document's
             own, so they stay here; the GL impact box below them is the shared summary. -->
        @if (showAccounting()) {
          <p-panel [header]="'ApDocument_Section_Accounting' | translateWF: 'Accounting'">
            <p-card>
              <div class="summary-rows">
                <div class="summary-rows__row row-start-center">
                  <span>{{ 'ApDocument_ApAccount' | translateWF: 'AP account' }}</span>
                  <span class="ml-auto">{{ effectiveApAccountNumber }} — {{ effectiveApAccountName | localized }}</span>
                </div>
                <div class="summary-rows__row row-start-center">
                  <span>{{ 'ApDocument_JournalEntry' | translateWF: 'Journal entry' }}</span>
                  <span class="ml-auto font-mono">{{ journalEntryDocumentNumber() }}</span>
                </div>
              </div>

              <span class="block mt-3 mb-2 text-[0.7rem] font-semibold uppercase tracking-wide text-surface-500">
                {{ 'ApDocument_GlImpact' | translateWF: 'GL impact' }}
              </span>
              <app-journal-entry-summary
                [references]="journalEntryReferences()"
                [reloadToken]="journalEntryReloadToken()"
                [currencyLabel]="currencyLabel(form.controls.currency.value)"
              />
            </p-card>
          </p-panel>
        }
      </div>
    </form>
  </div>
</main>

<!-- #4842/#5166 — ONE dialog for both directions, because there is one requirement flag. Either way a human
     is overruling what the line classifications derived, so either way the reason is mandatory and is stored
     with who and when; the direction decides whether it lands in the waived or the forced fields. -->
<p-dialog
  [visible]="withholdingDialogVisible()"
  (visibleChange)="withholdingDialogVisible.set($event)"
  [modal]="true"
  [style]="{ width: '32rem' }"
  [header]="
    withholdingIntent()
      ? ('ApDocument_Withholding_Force_Title' | translateWF: 'Require withholding')
      : ('ApDocument_Withholding_Waive_Title' | translateWF: 'Waive withholding requirement')
  "
>
  <p class="text-surface-600 mb-3">
    @if (withholdingIntent()) {
      {{
        'ApDocument_Withholding_Force_Help'
          | translateWF
            : 'This document will require a Constancia de Retención before it can be paid, whatever its line classifications
    say, and every line on it — goods included — becomes withholdable.'
      }}
    } @else {
      {{
        'ApDocument_Withholding_Waive_Help'
          | translateWF
            : 'This document will become payable without a Constancia de
    Retención, and no certificate can be issued against it.'
      }}
    }
  </p>
  <label for="withholdingReason" class="block mb-2">{{ 'ApDocument_Withholding_Waive_Reason' | translateWF: 'Reason' }}</label>
  <textarea
    id="withholdingReason"
    pTextarea
    rows="3"
    class="w-full"
    [formControl]="withholdingReason"
    [ngClass]="{ 'ng-invalid ng-dirty': withholdingReason.invalid && withholdingReason.touched }"
  ></textarea>

  <ng-template pTemplate="footer">
    <p-button severity="secondary" [text]="true" [label]="'Common_Cancel' | translateWF: 'Cancel'" (onClick)="withholdingDialogVisible.set(false)" />
    <p-button
      [label]="
        withholdingIntent() ? ('ApDocument_Withholding_Force_Confirm' | translateWF: 'Require withholding') : ('ApDocument_Withholding_Waive_Confirm' | translateWF: 'Waive')
      "
      [loading]="withholdingSaving()"
      [disabled]="withholdingReason.invalid"
      (onClick)="confirmWithholdingChange()"
    />
  </ng-template>
</p-dialog>

<!-- Cancelling a POSTED document. The reason is optional — the status already records that it was withdrawn,
     and who and when; this records why, which is the first thing an auditor asks about a cancelled fiscal
     number. What it cannot do is un-cancel: the button says so rather than the help text implying it. -->
<p-dialog
  [visible]="cancelDialogVisible()"
  (visibleChange)="cancelDialogVisible.set($event)"
  [modal]="true"
  [style]="{ width: '34rem' }"
  [header]="'ApDocument_Cancel_Title' | translateWF: 'Cancel this document'"
>
  <p class="text-surface-600 mb-3">
    {{
      'ApDocument_Cancel_Help'
        | translateWF
          : 'This reverses the journal entry and leaves the document cancelled for good. Its number and fiscal number stay
    used, and the amount comes back off the purchase orders it was invoiced against. To correct it, register a new
    document.'
    }}
  </p>
  <label for="cancelReason" class="block mb-2">{{ 'ApDocument_Cancel_Reason' | translateWF: 'Reason (optional)' }}</label>
  <textarea id="cancelReason" pTextarea rows="3" class="w-full" [formControl]="cancelReason"></textarea>

  <ng-template pTemplate="footer">
    <p-button severity="secondary" [text]="true" [label]="'Common_Cancel' | translateWF: 'Cancel'" (onClick)="cancelDialogVisible.set(false)" />
    <p-button severity="danger" [label]="'ApDocument_Cancel_Confirm' | translateWF: 'Cancel document'" [loading]="cancelSaving()" (onClick)="confirmCancel()" />
  </ng-template>
</p-dialog>
